import {
  ChakraFlex as Flex,
  FAIconName,
  FireflyButton,
  Icon,
  Text,
  cornerRadius,
  isAssessmentOverdue,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { AssessmentDisplayStatus } from './AssessmentDisplayStatus';
import { AssessmentFrequency } from './AssessmentFrequency';

export type CompleteTogetherBannerVariant = 'red' | 'yellow';

type Props = {
  assessmentResponseId: string;
  frequency: AssessmentFrequency;
  status: AssessmentDisplayStatus;
  epochCreatedDate: number;
  assignee?: string;
  responseUpdatedBy: string | null;
  onCompleteTogetherClick: (
    assessmentResponseId: string,
    bannerVariant: CompleteTogetherBannerVariant,
  ) => void;
};
const AssessmentCardActionMessage = ({
  assessmentResponseId,
  assignee,
  epochCreatedDate,
  frequency,
  onCompleteTogetherClick,
  status,
  responseUpdatedBy,
}: Props) => {
  let bgColor = 'critical.background';
  let bannerVariant: CompleteTogetherBannerVariant = 'red';
  let text = '';
  let icon = <></>;
  if (
    status === AssessmentDisplayStatus.COMPLETE ||
    status === AssessmentDisplayStatus.INCOMPLETE
  ) {
    return <></>;
  }

  const ageInDays = DateTime.fromMillis(epochCreatedDate).diffNow('days').days;
  const readableAgeInDays = Math.floor(Math.abs(ageInDays));
  const dayOrDays = readableAgeInDays === 1 ? 'day' : 'days';
  if (
    frequency !== AssessmentFrequency.Weekly &&
    ageInDays < -7 &&
    ageInDays >= -11
  ) {
    bgColor = 'tertiary.marigold.background';
    bannerVariant = 'yellow';
    icon = (
      <Icon
        color="tertiary.marigold.text"
        name={FAIconName.infoCircleRegular}
      />
    );
    if (status === AssessmentDisplayStatus.NEW) {
      text = `This assessment was sent ${
        assignee ? `to ${assignee} ` : ''
      }${readableAgeInDays} ${dayOrDays} ago`;
    } else if (status === AssessmentDisplayStatus.IN_PROGRESS) {
      text = `This assessment was started ${
        responseUpdatedBy ? `by ${responseUpdatedBy} ` : ''
      }${readableAgeInDays} ${dayOrDays} ago`;
    }
  } else if (isAssessmentOverdue(epochCreatedDate, frequency)) {
    bgColor = 'critical.background';
    bannerVariant = 'red';
    text = `This assessment is overdue by ${readableAgeInDays} ${dayOrDays}`;
    icon = (
      <Icon
        color="critical.base"
        name={FAIconName.exclamationTriangleRegular}
      />
    );
  }

  if (text === '') {
    return <></>;
  }
  return (
    <Flex
      alignItems="center"
      background={bgColor}
      borderRadius={cornerRadius.normal}
      data-testid="assessment-card-action-message-container"
      gap="8px"
      gridArea="action-message"
      height="45px"
      justifyContent="space-between"
      marginTop="18px"
      padding="12px 16px"
    >
      <Flex alignItems="center">
        {icon}
        <Text marginLeft="8px">{text}</Text>
      </Flex>
      <FireflyButton
        id="assessment-card-action-message-complete-together-button"
        leftIcon={FAIconName.penLine}
        onClick={() =>
          onCompleteTogetherClick(assessmentResponseId, bannerVariant)
        }
        size="small"
        variant="tertiary"
      >
        Complete together
      </FireflyButton>
    </Flex>
  );
};

export default AssessmentCardActionMessage;
